import { useCallback } from 'react'
import {
  useNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from 'react-router-dom'
import type { AuthUser, AuthUserClient } from '../types/auth.types'

const SESSION_LAST_TENANT_KEY = 'lastTenantId'

export function userCanAccessTenantId(
  user: AuthUser,
  tenantId: number
): boolean {
  if (user.superAdmin) {
    if (tenantId === -1) return true
    return user.clients.some((c) => c.id === tenantId)
  }
  return user.clients.some((c) => c.enabled && c.id === tenantId)
}

export function getDefaultTenantIdForUser(user: AuthUser): number {
  if (user.superAdmin) {
    const nonGlobal = user.clients.find((c) => c.id !== -1)
    return nonGlobal?.id ?? -1
  }
  const enabled = user.clients.find((c) => c.enabled)
  return enabled?.id ?? user.clients[0]?.id
}

export function getSessionLastTenantId(): number | null {
  try {
    const raw = sessionStorage.getItem(SESSION_LAST_TENANT_KEY)
    if (raw == null || raw === '') return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function setSessionLastTenantId(tenantId: number): void {
  try {
    sessionStorage.setItem(SESSION_LAST_TENANT_KEY, String(tenantId))
  } catch(err) {
    console.error(err)
  }
}

export function clearSessionLastTenantId(): void {
  try {
    sessionStorage.removeItem(SESSION_LAST_TENANT_KEY)
  } catch(err) {
    console.error(err)
  }
}

export function resolveBootstrapTenantId(
  user: AuthUser,
  selectedClient: AuthUserClient | null
): number {
  if (
    selectedClient?.id != null &&
    userCanAccessTenantId(user, selectedClient.id)
  ) {
    return selectedClient.id
  }
  const remembered = getSessionLastTenantId()
  if (remembered != null && userCanAccessTenantId(user, remembered)) {
    return remembered
  }
  return getDefaultTenantIdForUser(user)
}

export function getTenantIdFromPathname(pathname: string): number | null {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  if (!first) return null
  const n = parseInt(first, 10)
  return Number.isFinite(n) ? n : null
}

export function getTenantIdFromWindow(): number | null {
  if (typeof window === 'undefined') return null
  return getTenantIdFromPathname(window.location.pathname)
}

/** Tenant id from Admin Client Setup URLs: `/clients/client/:tenantid/...` */
export function getClientConfigTenantIdFromPathname(
  pathname: string
): number | null {
  const match = pathname.match(/^\/clients\/client\/(\d+)(?:\/|$)/)
  if (!match) return null
  const n = parseInt(match[1], 10)
  return Number.isFinite(n) ? n : null
}

export function stripTenantIdPrefix(pathname: string): string {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length === 0) return '/'
  const maybeTenant = parseInt(segments[0], 10)
  if (!Number.isFinite(maybeTenant)) return normalized
  const rest = segments.slice(1).join('/')
  return rest ? `/${rest}` : '/'
}

export function withTenantIdInPathname(
  pathname: string,
  tenantId: number
): string {
  const base = stripTenantIdPrefix(pathname)
  if (base === '/') return `/${tenantId}`
  return `/${tenantId}${base.startsWith('/') ? base : `/${base}`}`
}

export function appendTenantIdToPath(
  path: string,
  preferredTenantId?: number | null
): string {
  const tid =
    typeof preferredTenantId === 'number' && Number.isFinite(preferredTenantId)
      ? preferredTenantId
      : getTenantIdFromWindow()
  if (tid == null) return path
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path)
      u.pathname = withTenantIdInPathname(u.pathname, tid)
      return u.toString()
    } catch {
      return path
    }
  }
  const hashIndex = path.indexOf('#')
  const hash = hashIndex === -1 ? '' : path.slice(hashIndex)
  const noHash = hashIndex === -1 ? path : path.slice(0, hashIndex)
  const q = noHash.indexOf('?')
  const pathOnly = q === -1 ? noHash : noHash.slice(0, q)
  const searchOnly = q === -1 ? '' : noHash.slice(q)
  return `${withTenantIdInPathname(pathOnly || '/', tid)}${searchOnly}${hash}`
}

function shouldSkipTenantForPath(pathname: string): boolean {
  return (
    pathname.startsWith('/logout') ||
    pathname.startsWith('/error') ||
    pathname.startsWith('/docuSign') ||
    pathname.startsWith('/thank-you-page')
  )
}

export function withTenantInTo(to: To, preferredTenantId?: number | null): To {
  if (typeof to === 'string') {
    if (!to.startsWith('/') || shouldSkipTenantForPath(to)) return to
    return appendTenantIdToPath(to, preferredTenantId)
  }

  const next = { ...to }
  const pathname = typeof next.pathname === 'string' ? next.pathname : ''
  if (!pathname || !pathname.startsWith('/') || shouldSkipTenantForPath(pathname)) {
    return next
  }
  next.pathname = appendTenantIdToPath(pathname, preferredTenantId)
  return next
}

export function navigateWithTenant(
  navigate: NavigateFunction,
  to: To | number,
  options?: NavigateOptions,
  preferredTenantId?: number | null
): void {
  if (typeof to === 'number') {
    navigate(to)
    return
  }
  navigate(withTenantInTo(to, preferredTenantId), options)
}

export function useTenantNavigate(
  preferredTenantId?: number | null
): NavigateFunction {
  const navigate = useNavigate()
  return useCallback(
    ((to: To | number, options?: NavigateOptions) => {
      navigateWithTenant(navigate, to, options, preferredTenantId)
    }) as NavigateFunction,
    [navigate, preferredTenantId]
  )
}

export function openWithTenant(
  url: string,
  target?: string,
  features?: string
): Window | null {
  const nextUrl =
    typeof url === 'string' && url.startsWith('/')
      ? appendTenantIdToPath(url)
      : url
  return window.open(nextUrl, target, features)
}
